// Renders a question stem or option that mixes prose with INLINE maths.
//
// WHY: `render_spec: {kind:'math'}` carries a DISPLAY equation shown under the
// stem, which covers "what is the value of <fraction>". It does not cover maths
// embedded mid-sentence -- "The area of the triangle shown is 36m^2." or
// "Which of the following is equal to (2^7+3^8)^2?" -- where the notation is
// part of the prose and of each option. Those were rendering as literal
// "36m^{2}" to moderators and kids.
//
// DELIMITER: `\( ... \)`, not `$ ... $`. AMC word problems are full of money
// ("costs $5"), so a dollar delimiter would split real sentences in half.
import { Fragment } from 'react';

import { AcademyMath } from './AcademyMath';

// Non-greedy so consecutive spans in one sentence stay separate.
const INLINE = /\\\((.+?)\\\)/g;

export function AcademyRichText({
  text,
  className,
}: {
  // Nullable because AcademyQuestion.stem_text is: an image-only question has no
  // stem at all. Handled here so no call site needs its own guard.
  text: string | null | undefined;
  className?: string;
}) {
  if (!text) return null;
  if (!text.includes('\\(')) {
    return <span className={className}>{text}</span>;
  }

  const parts: Array<{ math: boolean; value: string }> = [];
  let cursor = 0;
  for (const match of text.matchAll(INLINE)) {
    const start = match.index ?? 0;
    if (start > cursor) parts.push({ math: false, value: text.slice(cursor, start) });
    parts.push({ math: true, value: match[1] });
    cursor = start + match[0].length;
  }
  if (cursor < text.length) parts.push({ math: false, value: text.slice(cursor) });

  return (
    <span className={className}>
      {parts.map((part, index) => (
        <Fragment key={index}>
          {part.math ? <AcademyMath latex={part.value} /> : part.value}
        </Fragment>
      ))}
    </span>
  );
}
